import {
  PromotionVoucher,
  PromotionIdentifier,
  Roundel,
  DeliveryAndReturnContent,
  DeliveryAndReturnContentKey,
  LinkedAddonServicesContent,
  CustomPromotionVoucher,
} from './Product';

export interface ProductResponseType {
  data: Data;
  included?: Included[];
}

export interface Data {
  type: string;
  id: string;
  attributes: Attributes;
  relationships?: Relationships;
}

export interface Attributes {
  ean?: string; // TODO: remove this from root level as it is no longer needed, will require updating mocks and usage
  name: string;
  brand?: Brand;
  productId?: number; // Might be used in the future
  site?: string;
  htmlDescription: string;
  unitOfMeasureIndicator: boolean;
  unitOfMeasureUnit?: string;
  unitOfMeasureValue?: number;
  weeeEligibilityInd?: boolean;
  images: Image[];
  variants: Variant[];
  sellable: boolean;
  returnable: string;
  swatches?: RootSwatch[];
  features?: Feature[];
  videos?: Video[];
  personalizableMaxLength?: number;
  digitalDownload?: boolean;
  tradeInValue?: number;
  tradingDepartmentId?: number;
  inventoryFailed?: boolean | undefined;
  priceFailed?: boolean | undefined;
  deliveryFailed?: boolean | undefined;
  productFactFailed?: boolean | undefined;
  reviewsFailed?: boolean | undefined;
  promotionIdentifierFailed?: boolean | undefined;
  productAttributesFailed?: boolean | undefined;
  insuranceRetreivalFailed?: boolean | undefined;
  dataAppleRiverContent?: string;
}

export interface RootSwatch {
  image: string;
  color: string;
}

export interface Brand {
  name: string;
}

export interface Image {
  identifier: string;
  tags: Tags;
}

export interface Tags {
  sequence: string;
  angle: string;
  iteration: string;
  type: string;
}

export interface Feature {
  name: string;
  sequence: number;
}

export interface Video {
  orientation: string;
  url: string;
}

export interface Variant {
  option: string;
  manufacturerPartNumber?: string;
  options: Options;
  itemNumber: string;
  price: Price;
  ean?: string;
  maxOrderQty?: number;
  carrierCode?: string;
  weightCode?: number;
  restrictedDeliveryInd?: number;
  packedWidth?: number;
  packedLength?: number;
  packedWeight?: number;
  packedDepth?: number;
  productRangeId?: number;
  swatches: VariantSwatch[];
  images: Image[];
  stock: VariantStock;
  ffmIndicator?: boolean;
  insuranceOffers?: InsuranceOffer[];
  deliveryAndReturnContentKey: DeliveryAndReturnContentKey;
  linkedAddOnService?: string[];
  productTypeCode: number;
}

type InsuranceOffer = {
  priority: number;
  offer: {
    productId: number;
    productContentId: number;
    sellingPrice: number;
    weeklyPrices?: {
      numberOfWeeks: number;
      weeklySellingPrice: number;
    }[];
    isAutoRenewable: boolean;
    productTypeCode: number;
    productLineId: string;
    sku: string;
    suffix: string;
    outletCode: string;
    productDescription: string;
    coverLengthMonths: number;
    termDescription: string;
  };
};

export interface VariantSwatch {
  image: string;
  colour?: string;
}

export interface VariantStock {
  status: string;
  code: StockCode;
  productTypeInd: string;
  stockAvailabilityCode: string;
  holdDetails?: VariantStockHoldDetails;
}

export interface VariantStockHoldDetails {
  date?: string;
  period?: number;
  periodUnit?: string;
}

export interface Options {
  colour: string;
  size?: string;
}

export interface Price {
  validFrom: string;
  validUntil: string;
  amount: Amount;
  cutOffTime?: number;
  minOrderValue?: MinOrderValue;
  maxOrderValue?: MaxOrderValue;
}

export interface Amount {
  currencyCode: string;
  current: number;
  currencySymbol: string;
  previousTo?: number;
  previousFrom?: number;
  currentTo?: number;
  currentFrom?: number;
  fractionDigits: number;
  previous?: number;
  saving?: number;
}

export interface Relationships {
  inventory?: Inventory;
  delivery?: Delivery;
  productreview?: Productreview;
  promotionvoucher?: Promotionvoucher;
  roundel?: RoundelRelation;
}

export interface Inventory {
  links: Links;
  data?: DataInRelations;
}

export interface Delivery {
  links?: Links;
  data?: DataInRelations;
}

export interface Productreview {
  links: Links;
  data: DataInRelations;
}

export interface Promotionvoucher {
  links: Links;
  data?: DataInRelations;
}

export interface RoundelRelation {
  links: Links;
  data?: DataInRelations;
}

export interface Links {
  related: string;
}

export interface DataInRelations {
  type: string;
  id: string;
}

export interface Included {
  type?: string;
  id?: string;
  attributes: AttributeObject;
}

export interface Attribute {
  code?: string;
  option?: string;
  holdDetails?: HoldDetails;
}

export interface PriceNow {
  startDate: string;
  endDate: string;
  currencyCode: string;
  basePrice: number;
  currencySymbol: string;
  origPrice?: number;
  suffixCode: string;
  vatCode: string;
  outletCode: string;
  prfValue?: number;
  exPrf?: number;
  productLevel: {
    wasFrom?: number;
    wasTo?: number;
    nowFrom?: number;
    nowTo?: number;
    saveUpToPrice?: number;
    savePrice?: number;
    prfValue?: number;
    exPrf?: number;
  };
  priceTerms?: PriceTerm[];
  longestWeeklyPriceTermWithZeroInterest?: PriceTerm;
  longestWeeklyPriceTermWithInterest?: PriceTerm;
  has208WeeklyPriceTerm?: boolean;
}

export interface PriceTerm {
  apr: number;
  termPeriod: number;
  totalSellingPrice: number;
  weeklyPaymentAmount: number;
}

export interface SkuPrice {
  optionNumber: string;
  now: PriceNow;
}
export interface AttributeObject {
  skus: Sku[];
  extractDate?: Date;
  site?: string;
  productId: string;
  reviewsCount?: number;
  averageStars?: number;
  skuPrices: SkuPrice[];
  productAttributes?: ProductAttribute[];
  promotionvoucher?: PromotionVoucher | CustomPromotionVoucher;
  roundel?: Roundel;
  content: DeliveryAndReturnContent;
  delivery: {
    type: string;
    options?: Option[];
  };
  includedInsuranceContent: InsuranceContent[];
  linkedAddOnServices: LinkedAddonServicesContent[];
  activePromotionIdentifier?: PromotionIdentifier;
}

export interface Option {
  code: string;
  prices: Price[];
}

export interface MinOrderValue {
  currencyCode: string;
  current: number;
  fractionDigits: number;
}

export interface MaxOrderValue {
  currencyCode: string;
  current: number;
  fractionDigits: number;
}

export interface HoldDetails {
  date?: string;
  period?: number;
}
export interface Sku {
  code: string;
  option: string;
  holdDetails?: HoldDetails;
}

export interface ProductAttribute {
  key: string;
  value: string[];
}

export type DescriptionItemBefore = {
  boldText?: string;
  text?: string;
  linkUrl?: string;
  linkText?: string;
  policyMeetsDemandsText?: string;
  fullListText?: string;
  termsUrl?: string;
  termsAndcondition?: string;
  makeClaimText?: string;
  productInfoDocUrl?: string;
  productInfoDocText?: string;
  relevanyInfoUrl?: string;
  relevantInfoText?: string;
  additionalInfoText?: string;
  excludesText?: string;
  insuranceRelevantInfo?: string;
  termsText?: string;
  insuranceRelevantInfoUrl?: string;
};

export type DescriptionItemAfter = {
  boldText?: string;
  text?: string;
  linkUrl?: string;
  linkText?: string;
  policyMeetsDemandsText?: string;
  fullListText?: string;
  confirmReadText?: string;
  termsUrl?: string;
  termsAndcondition?: string;
  additionalYearlyUrl?: string;
  additionalMonthlyUrl?: string;
  productInfoDocLinkText?: string;
  productInfoYearlyUrl?: string;
  productInfoMonthlyUrl?: string;
  relevantInfoText?: string;
  additionalInfoUrl?: string;
  recievePolicyDocText?: string;
  monthlyChooseText?: string;
  monthlyCancelText?: string;
  insuranceInfoDoc?: string;
  insuranceRelevantInfo?: string;
  furtherInfoUrl?: string;
  termsYearlyUrl?: string;
  relevantInfoYearlyUrl?: string;
  termsText?: string;
  termsMonthlyUrl?: string;
};

type CheckIfRightMonthlyObjectItem = {
  text?: string;
  type?: string;
  url?: string;
};

type CheckIfRightMonthlyObject = CheckIfRightMonthlyObjectItem[][];
type CheckIfRightYearlyObject = CheckIfRightMonthlyObjectItem[][];

type InsuranceDialogItem = {
  text: string;
  type: string;
};

type InsuranceDialog = {
  value: InsuranceDialogItem[];
  covered: {
    text: string;
    bulletText: string[];
  };
  notCovered: {
    text: string;
    bulletText: string[];
  };
};

export type InsuranceValue = {
  text?: string;
  type?: string;
  url?: string;
  title?: string;
  bulletText?: [];
};

type PdpInsuranceDescriptionItem = {
  title?: string;
  text?: string;
  type?: string;
  bulletText?: string[];
};

type PdpInsuranceDescription = PdpInsuranceDescriptionItem[][];

type InsuranceContentItem = {
  value?: InsuranceValue[][];
  valueAfter?: InsuranceValue[][];
  covered?: {
    text: string;
    bulletText: string[];
  };
  notCovered?: InsuranceValue;
  checkIfRightYearly?: CheckIfRightYearlyObject;
  checkIfRightMonthly?: CheckIfRightMonthlyObject;
  insuranceDialog?: InsuranceDialog;
  pdpInsuranceDescription: PdpInsuranceDescription;
};

export type InsuranceContent = {
  id: string;
  insuranceContent: InsuranceContentItem;
};

export enum StockCode {
  OUTOFSTOCK = 'OUTOFSTOCK',
  UNAVAILABLE = 'UNAVAILABLE',
  DCSTOCK = 'DCSTOCK',
  LOWSTOCK = 'LOWSTOCK',
  HOLDDAY = 'HOLDDAY',
  HOLDWEEKS = 'HOLDWEEKS',
  HOLDDATE = 'HOLDDATE',
  DDSTOCKWORKINGDAYS = 'DDSTOCKWORKINGDAYS',
  DDSTOCKWEEK = 'DDSTOCKWEEK',
  DDSTOCKDATE = 'DDSTOCKDATE',
  DDSTOCKNEXTDATE = 'DDSTOCKNEXTDATE',
  DDSTOCK = 'DDSTOCK',
  PREORDER = 'PREORDER',
}
